// 付箋・テキスト・図形ラベルなどの編集モードを担うフック。
//
// 振る舞い:
// - ダブルクリックで編集モードに遷移し、対象要素を contentEditable にする
// - IME（CompositionEvent）中は op を送らず、composition 確定後に変更を反映
// - キー入力に対しては 50ms debounce で object.text.update op を送信する
// - blur で編集モードを終了する
//
// 50ms debounce は同時編集体験を担保するためのバランス（README §11）。
//
// markdown オプション（付箋・テキスト図形で true）:
// - 編集中も Markdown 記法（見出し・箇条書き）を即時 WYSIWYG 反映する（マーカー文字は画面から消える）。
// - 保存は生 Markdown のまま。編集領域 DOM は「1 行 = 1 ブロック」の正準構造とし、記法マーカーは
//   data-prefix（隠し）+ ::before（グリフ）へ逃がす（markdown-wysiwyg.ts）。
// - Enter/削除/貼付/カット/Undo などの構造変更は beforeinput / clipboard で横取りし、
//   「生テキスト + 選択の純粋変換 → 正準ブロック再構築」に統一して data-prefix を壊さない。
// - Undo/Redo は自前スナップショットスタックが正本（ネイティブ委任は DOM/IME 最適化であって履歴とは無関係）。
// - markdown を渡さない種別（セクション等）は従来コードパスと完全一致（退行なし）。
//
// linkifySelectionOnUrlPaste オプション（付箋・テキスト図形で true）:
// - 範囲選択したまま単一の http(s) URL を貼り付けると、選択部分を [表示テキスト](URL) へ変換する。
//   変換は domain の linkifyMarkdownRange が担い、部分書式（run）は表示文字へ付いたまま残る。

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import {
  TEXT_BODY_MAX_LENGTH,
  buildMarkdownSourceMap,
  clipRangeToMarkdownVisible,
  linkifyMarkdownRange,
  planPlainLineLinkReplacement,
  rebaseTextInlineStyles,
  type CanvasOp,
  type TextInlineStyle,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";
import { useShowToast } from "../notice/use-show-toast";
import {
  fitTextBodyForSend,
  textBodyFitNotice,
  type TextBodyFitResult,
} from "./text-body-send-fit";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { toggleTextRangeBold } from "../text-range-bold";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";
import { CARET_KEEPALIVE_INTERVAL_MS, CARET_KEEPALIVE_TICK_MS } from "../remote-transient-timing";
import { findUrlTokenAt, isHttpUrl } from "../../../lib/linkify-text";
import { htmlFragmentToLinkText } from "../../../lib/paste-links";
import { isLikelyPasteUrl } from "../../../lib/paste-url";
import { BULLET_MAX_LEVEL } from "../../../lib/markdown-text";
import {
  buildMarkdownEditorDom,
  changeListIndent,
  deleteBackward,
  deleteForward,
  deleteRange,
  editorNeedsRebuild,
  insertPlainLineBreak,
  insertTextAt,
  isBackspaceBoundary,
  isDeleteForwardBoundary,
  rawSelectionFromRange,
  restoreEditorCaretOffset,
  restoreEditorSelection,
  saveEditorCaretOffset,
  saveEditorSelection,
  serializeMarkdownEditorDom,
  splitForEnter,
  type EditorSelection,
  type EditorTextAndCaret,
} from "../../../lib/markdown-wysiwyg";
import { TEXT_RANGE_PANEL_ATTRIBUTE } from "../toolbar/text-range-panel-attribute";
import { useBoundedTextSelection } from "./use-bounded-text-selection";

interface Options {
  readonly objectId: string;
  readonly initialText: string;
  readonly elementRef: RefObject<HTMLElement | null>;
  readonly dispatcher: CanvasOpDispatcher;
  /** ロック中など編集を抑止したい場合に true を渡す。 */
  readonly disabled?: boolean | undefined;
  /**
   * 本文が空（トリム後 0 文字）で編集を確定したときに呼ばれる。テキスト図形の「0 文字なら自動削除」に使う。
   * このコールバックを渡さない種別（付箋など）では、空でも削除しない。
   */
  readonly onEmptyCommit?: (() => void) | undefined;
  /**
   * 編集中に本文が変化したとき（入力・IME 確定ごと）に、その時点の生テキストで呼ばれる。
   * テキスト図形のように「入力中も本文量に応じて表示（図形枠の自動拡張など）を追従させたい」
   * 種別が、確定を待たずに再計算するために使う。渡さない種別では何も起きない。
   * IME 変換中（composition 中）は呼ばれず、確定時のみ発火するためジッターしない。
   */
  readonly onLiveText?: ((text: string) => void) | undefined;
  /**
   * 付箋・テキスト図形の本文を編集中も Markdown 記法で WYSIWYG 整形する（マーカーは非表示）。
   * 未指定（セクション等）では従来どおり生テキストをそのまま編集する。
   */
  readonly markdown?: boolean | undefined;
  /**
   * リンク付き HTML の貼り付けを [表示テキスト](URL) へ変換して取り込むか。
   * 非編集表示が LinkifiedText（Markdown 形式リンクを描画できる）を使う種別（付箋・テキスト）だけが
   * opt-in する。素のテキスト描画の種別（セクションタイトル等）で有効にすると、変換結果の
   * [ラベル](URL) が生の記法のまま見えてしまうため既定は無効。
   */
  readonly preserveHtmlAnchorsOnPaste?: boolean | undefined;
  /**
   * 範囲選択中に単一の http(s) URL を貼り付けたとき、選択範囲を `[表示テキスト](URL)` へ
   * 変換する。非編集表示が LinkifiedText である付箋・テキスト図形・セクションだけで有効にする。
   * Markdown を使わないセクションは、編集中のみ記法を生テキストとして表示する。
   */
  readonly linkifySelectionOnUrlPaste?: boolean | undefined;
  /**
   * テキスト図形の部分書式（inline style run）。編集 DOM の span 分割と、本文変更に伴う
   * run の追従（rebase）に使う。渡さない種別（付箋・セクション等）では部分書式を扱わない。
   */
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  /** 部分書式の差分計算の基準になる基底書式（テキスト図形の color / fill / fontSize / bold）。 */
  readonly baseStyle?: TextInlineStyle | undefined;
  /**
   * 非空な文字範囲選択を Canvas UI store の `activeTextRangeSelection` へ反映するか。
   * 範囲専用書式パネルを持つテキスト図形だけが opt-in する。
   */
  readonly rangeStyle?: boolean | undefined;
}

interface Result {
  readonly isEditing: boolean;
  readonly enterEditMode: () => void;
  readonly leaveEditMode: () => void;
  readonly editorProps: {
    readonly contentEditable: boolean;
    readonly suppressContentEditableWarning: boolean;
    readonly onInput: (event: React.FormEvent<HTMLElement>) => void;
    readonly onCompositionStart: () => void;
    readonly onCompositionEnd: (event: React.CompositionEvent<HTMLElement>) => void;
    readonly onBlur: (event: React.FocusEvent<HTMLElement>) => void;
    readonly onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    readonly onKeyUp: () => void;
    readonly onClick: (event: React.MouseEvent<HTMLElement>) => void;
    readonly onPaste: (event: React.ClipboardEvent<HTMLElement>) => void;
    readonly onCut: (event: React.ClipboardEvent<HTMLElement>) => void;
    readonly onDrop: (event: React.DragEvent<HTMLElement>) => void;
    readonly onDragStart: (event: React.DragEvent<HTMLElement>) => void;
  };
}

/** Undo/Redo スナップショット（生テキスト + 部分書式 + キャレット）。 */
interface UndoSnapshot {
  readonly text: string;
  readonly inlineStyles: readonly TextInlineStyleRange[];
  readonly caret: number;
}

/** 直近の履歴操作から本間隔（ms）以内の 2 回目の Undo/Redo は二重発火として無視する。 */
const HISTORY_DEDUP_MS = 20;

/**
 * その op が、対象オブジェクトの `textStyleRevision` を進める未確定更新かどうか。
 *
 * revision は「本文・部分書式・全文書式」で共有する競合境界であり、進めるのは本文更新
 * （`object.text.update`）だけではない。書式 op（`object.text.style.update`）を数え忘れると、
 * 楽観的に +1 した revision を編集元として本文更新を送ってしまい、サーバ側で本文更新が先に
 * 処理された場合に revision 不一致の競合 reject となって入力が巻き戻る（op ごとに別 Lambda で
 * 処理されるため到着順は保証されない）。両方を 1 件へ直列化する述語としてここへ集約する。
 */
function isUnackedTextRevisionOp(op: CanvasOp, objectId: string): boolean {
  if (op.type !== "object.text.update" && op.type !== "object.text.style.update") return false;
  return op.objectId === objectId;
}

/**
 * IME 変換確定（compositionend）直後の本間隔（ms）以内に届く Enter / 改行入力は、変換確定の Enter が
 * isComposing=false で漏れてきたものとみなして無視する（箇条書きの誤継続/終了を防ぐ）。ブラウザ差の取りこぼし対策。
 */
const COMPOSITION_ENTER_GUARD_MS = 50;

/**
 * IME 変換開始（compositionstart）時点の選択と本文長。変換を取り消したときに Chrome が選択を 0 レンジにして
 * キャレットが失われる不具合への対策で、compositionend の復元先を求めるために使う（生テキストのオフセット）。
 */
interface CompositionStartRecord {
  /** 変換開始時の選択の終端。 */
  readonly end: number;
  /** 変換開始時の生テキスト全体の長さ。 */
  readonly rawLength: number;
  /** 変換開始時の選択の始端。 */
  readonly start: number;
}

/**
 * 上限へ達した状態で入力を続けると 1 打鍵ごとに切り詰めが起きるため、通常入力とネイティブ委任入力の
 * 後始末では、同一の編集セッション内で本間隔（ms）に 1 回までへお知らせを間引く。
 * 貼り付け・ドロップなどの構造変更は間引かない（実際に増えるかはお知らせストアの重複抑止に委ねる）。
 */
const TEXT_FIT_NOTICE_THROTTLE_MS = 5000;

const DEBOUNCE_MS = 50;
// 編集中の自分のキャレット位置を他参加者へ中継する最小送信間隔（ms）。
// use-cursor-broadcaster と同じ約 10Hz に間引いて通信量を抑える。
const CARET_BROADCAST_INTERVAL_MS = 100;

export function useObjectTextEditor(options: Options): Result {
  const { objectId, initialText, elementRef, dispatcher, disabled, onEmptyCommit, onLiveText } =
    options;
  const isMarkdown = options.markdown ?? false;
  const preserveHtmlAnchorsOnPaste = options.preserveHtmlAnchorsOnPaste ?? false;
  const linkifySelectionOnUrlPaste = options.linkifySelectionOnUrlPaste ?? false;
  const isRangeStyleEnabled = options.rangeStyle ?? false;
  const showToast = useShowToast();
  const setActiveTextRangeSelection = useCanvasStore((s) => s.setActiveTextRangeSelection);
  // 本文編集中の Ctrl+B で範囲書式を適用したときに、範囲専用パネルからの適用と同じく
  // 「変更前の run」を編集中 Undo 履歴へ積ませるための通知。
  const notifyTextRangeStyleApplied = useCanvasStore((s) => s.notifyTextRangeStyleApplied);
  // 範囲専用パネルからの書式適用の通知カウンタ。値そのものは effect の中でだけ使うため、
  // 毎レンダー更新する ref に写して effect の依存へは載せない（他の run 変化で張り直さない）。
  const rangeStyleAppliedSignal = useCanvasStore((s) => s.textRangeStyleAppliedSignal);
  const rangeStyleAppliedSignalRef = useRef(rangeStyleAppliedSignal);
  rangeStyleAppliedSignalRef.current = rangeStyleAppliedSignal;
  /** 直前に履歴へ反映した通知カウンタ。編集開始時に現在値へそろえる。 */
  const lastRangeStyleSignalRef = useRef(rangeStyleAppliedSignal);
  // 権威 run と基底書式は毎レンダーで更新する ref に保持し、effect の依存へは載せない
  // （op の確定ごとに参照が変わるため、依存へ載せると編集中に effect が張り直される）。
  const authoritativeInlineStylesRef = useRef<readonly TextInlineStyleRange[]>(
    options.inlineStyles ?? []
  );
  authoritativeInlineStylesRef.current = options.inlineStyles ?? [];
  const baseStyleRef = useRef<TextInlineStyle>(options.baseStyle ?? {});
  baseStyleRef.current = options.baseStyle ?? {};
  // 編集中のローカル run（本文変更に追従させた最新値）。編集開始時に権威値で初期化する。
  const runsRef = useRef<readonly TextInlineStyleRange[]>(options.inlineStyles ?? []);
  /**
   * 直前に把握していた本文。ネイティブ委任入力（通常タイプ・IME 確定）では変更前の本文が
   * イベントから得られないため、run の rebase の基準としてここへ保持する。
   */
  const lastSerializedTextRef = useRef(initialText);
  // 部分書式を送るのは、run を扱う種別（テキスト図形）だけ。
  const sendsInlineStyles = options.inlineStyles !== undefined;
  // 参照専用（埋め込み）ではテキスト編集（ダブルクリックでの contentEditable 遷移・updateText 送信）を
  // 抑止する。ロック（disabled）と同様に扱う。
  const readOnly = useCanvasStore((s) => s.readOnly);
  const isTrashed = useCanvasDocStore((s) => s.meta.isTrashed ?? false);
  const isDisabled = (disabled ?? false) || readOnly;
  const [isEditing, setEditing] = useState(false);
  // leaveEditMode のガード用に編集状態を同期的にも保持する。
  // 非編集時の表示は React 管理の要素（LinkifiedText の <a> / <span>）で描画されるが、
  // <a> はフォーカス可能なため、リンククリック→新規タブ遷移などで「編集していないのに」
  // focusout（onBlur）や Escape の keydown がエディタ要素から発火し得る。そのまま
  // leaveEditMode を実行すると el.textContent = "" が React 管理下の子ノードを直接消し、
  // 表示消失と、後続 reconcile での removeChild 例外（NotFoundError→エラー画面）を招く。
  const isEditingRef = useRef(false);
  const composingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 同時編集の収束用: 「最後に自分から送信した / リモートから適用した」テキスト。
  // 編集中に他者の確定で initialText が変わったとき、ローカルの現在値がこの値と一致していれば
  // （＝未送信のローカル変更が無ければ）リモートの最新テキストを安全に適用できる、という判定に使う。
  const lastSyncedTextRef = useRef(initialText);

  // Undo/Redo（markdown モードの正本）。編集開始時にクリアする。
  const undoRef = useRef<UndoSnapshot[]>([]);
  const redoRef = useRef<UndoSnapshot[]>([]);
  // 連続タイプを 1 ステップへコアレスするための直前オペ種別。
  const lastOpKindRef = useRef<"none" | "type" | "struct">("none");
  // Undo/Redo 適用中はスナップショットを積まない（二重記録防止）。
  const applyingHistoryRef = useRef(false);
  // keydown と beforeinput の両方から Undo/Redo が来たときの二重発火防止用の直前履歴操作。
  // 抑止は「同じ種別が短時間に連続したとき」だけに限る。時刻だけで判定すると、Undo の直後に
  // 押した Redo（種別が違う操作）まで飲み込んでしまい、取り消しが戻せなくなる。
  const lastHistoryRef = useRef<{ kind: "undo" | "redo"; at: number }>({ at: 0, kind: "undo" });
  // IME 変換確定（compositionend）時刻。直後に漏れてくる Enter を抑止するために使う。
  const compositionEndedAtRef = useRef(0);
  // 直前の keydown が IME 変換中に発火したものか（markdown のみ使用）。keydown は対応する
  // beforeinput より必ず先行するため、beforeinput 側はこれで「変換確定の Enter が実 Enter として
  // 漏れてきた改行入力」を壁時計（COMPOSITION_ENTER_GUARD_MS）に依存せず識別できる。
  // 記録の有効期間はその物理キーストロークが生むイベント列まで（keyup で消費する。onKeyUp 参照）。
  const lastKeydownWasImeRef = useRef(false);
  // IME 変換開始時の選択と本文長（markdown のみ使用）。変換取り消しで Chrome が選択を 0 レンジにしたとき、
  // compositionend で「フォーカスはあるのに選択が要素内に無い」場合の復元先を求めるために使う。
  // ライフサイクル:
  // - 設定: onCompositionStart で必ず代入する（選択が取れない・要素が無いときは null。0 などの仮の値は入れない。
  //   全文先頭へ復元すると、続く入力が 1 行目の先頭へ入る元の不具合と同じ結果になるため）
  // - 消費: onCompositionEnd で、使ったかどうかに関わらず null へ戻す
  // - 失効: 編集セッション開始の effect・leaveEditMode・本文を置き換える経路（applyRemoteText /
  //   applyHistorySnapshot）で null へ戻す（記録した生オフセットが古くなるため）。effect の依存変化によって
  //   セッションの途中で消えた場合は「復元しない」安全側に倒れる
  const compositionStartRef = useRef<CompositionStartRecord | null>(null);
  // 直前に他参加者へ中継した内容（キャレット位置と、範囲選択中だったか）。
  // エディタ外へドラッグした後の「帯を消す 1 通」を 1 回だけに抑えるために使う。
  // null は「この編集セッションでまだ一度も送っていない」を表し、消すべき帯が無いことを意味する。
  const lastSentRef = useRef<{ caretIndex: number; hadSelection: boolean } | null>(null);
  // 文字選択のドラッグが編集面の外へ出たとき、最後に枠内で成立した選択を保つ（Issue #160）。
  // 選択を読んで範囲パネル・他参加者への中継へ流す処理は、読む前に reconcileSelection を呼び、
  // 枠外でブラウザーが広げた選択（本文の先頭まで）を保存値へ戻してから使う。
  const { reconcileSelection } = useBoundedTextSelection({
    elementRef,
    enabled: isEditing && !isDisabled,
    isComposing: () => composingRef.current,
  });

  // ---- テキスト読取・反映・キャレットの戦略（markdown 有無で差し替え）----
  const readEditor = useCallback(
    (el: HTMLElement): string => (isMarkdown ? serializeMarkdownEditorDom(el) : readEditorText(el)),
    [isMarkdown]
  );
  const writeEditor = useCallback(
    (el: HTMLElement, text: string, runs?: readonly TextInlineStyleRange[] | undefined): void => {
      if (isMarkdown) buildMarkdownEditorDom(el, text, runs ?? runsRef.current);
      else el.textContent = text;
    },
    [isMarkdown]
  );
  /**
   * 空本文・run 無しで実測した op.apply の封筒サイズ。送信サイズ上限の安価な事前判定に使う。
   * 対象・編集セッションごとに 1 回だけ測り、本文サイズに依存しない定数コストで済ませる。
   */
  const envelopeBytesRef = useRef<number | null>(null);
  /** 直前に切り詰めのお知らせを出した時刻（通常入力の間引き用）。編集開始時に 0 へ戻す。 */
  const lastFitNoticeAtRef = useRef(0);

  /**
   * 変更後の本文とキャレットを、文字数上限と送信サイズ上限の両方へ収める。
   * 切り詰めが起きたときだけ情報メッセージを出す（`kind` により間引く）。
   * 部分書式は「収めた本文に対応する run」を計算して返す（runsRef の更新は呼び出し側が行う）。
   *
   * `runsSource` は「どの本文に対応する run を追従させるか」を指定する。既定は現在の run
   * （＝ `prevText` に対応）で、履歴復元だけはスナップショットの run（＝ `nextText` に対応）を渡す。
   */
  const fitForSend = useCallback(
    (
      prevText: string,
      nextText: string,
      caret: number,
      kind: "type" | "struct",
      runsSource?: { readonly runs: readonly TextInlineStyleRange[]; readonly forText: string }
    ): TextBodyFitResult => {
      const source = runsSource ?? { forText: prevText, runs: runsRef.current };
      if (envelopeBytesRef.current === null) {
        envelopeBytesRef.current = dispatcher.measureTextUpdateMessageBytes(
          objectId,
          "",
          undefined
        );
      }
      const result = fitTextBodyForSend({
        caret,
        currentRuns: sendsInlineStyles ? source.runs : undefined,
        envelopeBytes: envelopeBytesRef.current,
        maxLength: TEXT_BODY_MAX_LENGTH,
        measureBytes: (text, runs) =>
          dispatcher.measureTextUpdateMessageBytes(objectId, text, runs),
        nextText,
        prevText,
        // サーバ・他参加者と同じ共有関数を使い、経路差で run がずれないようにする。
        rebaseRuns: (text) =>
          rebaseTextInlineStyles({
            base: baseStyleRef.current,
            nextText: text,
            prevText: source.forText,
            runs: source.runs,
          }),
        sendsInlineStyles,
      });
      const notice = textBodyFitNotice(result, TEXT_BODY_MAX_LENGTH);
      if (notice) {
        // 間引くのは「入りきらない分を切り詰めた」という情報メッセージだけにする。
        // 保存できなかったことを伝える warning まで間引くと、打鍵のたびに入力が巻き戻るのに
        // 理由が 5 秒に 1 回しか出ず、原因が分からないまま入力が消え続けて見える。
        const now = Date.now();
        const throttled =
          notice.level === "info" &&
          kind === "type" &&
          now - lastFitNoticeAtRef.current < TEXT_FIT_NOTICE_THROTTLE_MS;
        if (!throttled) {
          if (notice.level === "info") lastFitNoticeAtRef.current = now;
          showToast(notice.level, notice.message);
        }
      }
      return result;
    },
    [dispatcher, objectId, sendsInlineStyles, showToast]
  );

  const saveCaret = useCallback(
    (el: HTMLElement): number | null =>
      isMarkdown ? saveEditorCaretOffset(el) : saveCaretCharacterOffset(el),
    [isMarkdown]
  );
  const restoreCaret = useCallback(
    (el: HTMLElement, offset: number): void => {
      if (isMarkdown) restoreEditorCaretOffset(el, offset);
      else restoreCaretCharacterOffset(el, offset);
    },
    [isMarkdown]
  );

  // 無効化された場合はその瞬間に編集モードを終了し、保留中の debounce timer も破棄する。
  // 他クライアントから locked=true が反映された直後に古い text を送信して
  // ロック後のオブジェクトを書き換えてしまうのを防ぐ（dispatcher 側にも防御を入れている）。
  useEffect(() => {
    if (!isDisabled) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (isEditing) {
      // leaveEditMode と同じ理由で、非編集表示へ戻す前に contentEditable の未追跡 DOM をクリアする
      // （ロック等で編集が強制終了する経路でも二重化しないようにする）。
      if (elementRef.current) elementRef.current.textContent = "";
      isEditingRef.current = false;
      setEditing(false);
    }
  }, [isDisabled, isEditing, elementRef]);

  // 送信待ちの最新内容（本文 + run）。連続入力はここへ coalesce し、debounce で 1 通へまとめる。
  const pendingSendRef = useRef<{
    text: string;
    runs: readonly TextInlineStyleRange[] | undefined;
  } | null>(null);

  /**
   * 送信本体。部分書式を伴う本文更新は、対象オブジェクトごとに同時送信を 1 件へ制限する
   * （複数を並行送信すると、別 Lambda での処理順が入れ替わったときに編集元 revision が
   * 合わなくなり、後から送った編集が競合 reject で失われる）。未 ack が残っている場合は
   * 送らずに debounce を張り直し、ack 後の最初の機会で最新の本文・run を 1 通で送る。
   */
  const sendNow = useCallback(
    (text: string, runs: readonly TextInlineStyleRange[] | undefined) => {
      lastSyncedTextRef.current = text;
      // 付箋・セクション名など部分書式を持たない対象では、options 引数そのものを渡さない
      // （非 text 対象へ inline フィールドを載せないという op 契約をここで守る）。
      if (runs === undefined) {
        dispatcher.updateText(objectId, text);
        return;
      }
      dispatcher.updateText(objectId, text, { inlineStyles: runs });
    },
    [dispatcher, objectId]
  );

  /**
   * 自分が送った本文更新が未 ack か（対象オブジェクトのみ）。購読して真偽値の変化だけで
   * 再レンダーさせ、ack 後に範囲パネルの表示条件を再判定できるようにする。
   *
   * ここは送信の直列化（`hasUnackedTextUpdate`）と違い、意図して**本文更新だけ**を見る。
   * 範囲パネルを隠す理由は「保存済み本文と DOM がずれた状態で raw offset を当てると別の文字へ
   * 書式が付く」ことであり、本文を変えない書式 op はこの理由に当たらない。書式 op まで含めると、
   * 1 項目押すたびにパネルが 1 往復ぶん消えて連続操作（色 → サイズ → 太字）ができなくなる。
   */
  const hasInFlightTextUpdate = useCanvasDocStore((state) =>
    sendsInlineStyles
      ? state.inFlightOps.some(
          (op) => op.type === "object.text.update" && op.objectId === objectId
        )
      : false
  );
  const hasInFlightTextUpdateRef = useRef(false);
  hasInFlightTextUpdateRef.current = hasInFlightTextUpdate;

  const hasUnackedTextUpdate = useCallback((): boolean => {
    if (!sendsInlineStyles) return false;
    return useCanvasDocStore
      .getState()
      .inFlightOps.some((op) => isUnackedTextRevisionOp(op, objectId));
  }, [objectId, sendsInlineStyles]);

  const scheduleSend = useCallback(
    (text: string, runs?: readonly TextInlineStyleRange[] | undefined) => {
      pendingSendRef.current = { runs, text };
      if (timerRef.current) clearTimeout(timerRef.current);
      const flush = (): void => {
        timerRef.current = null;
        const pending = pendingSendRef.current;
        if (!pending) return;
        if (hasUnackedTextUpdate()) {
          // 未 ack の本文更新が残っている間は送らず、次の機会へ回す（同時送信は 1 件まで）。
          timerRef.current = setTimeout(flush, DEBOUNCE_MS);
          return;
        }
        pendingSendRef.current = null;
        sendNow(pending.text, pending.runs);
      };
      timerRef.current = setTimeout(flush, DEBOUNCE_MS);
    },
    [hasUnackedTextUpdate, sendNow]
  );

  const flushPending = useCallback(
    (text: string, runs?: readonly TextInlineStyleRange[] | undefined) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      // 未 ack の本文更新が残っている場合は、編集終了時でも即時送信しない。ここで 2 通目を
      // 送ると同時送信 1 件の制限を破り、別 Lambda での処理順が入れ替わったときに編集元
      // revision が合わず、最後の編集が競合 reject で失われる。確定後の最初の機会へ回す。
      if (hasUnackedTextUpdate()) {
        scheduleSend(text, runs);
        return;
      }
      pendingSendRef.current = null;
      // 送信した内容を「同期済み」として記録する。以降にサーバからエコーで戻ってくる同じ
      // initialText を、収束処理が「変化なし」として無視できるようにする。
      sendNow(text, runs);
    },
    [hasUnackedTextUpdate, scheduleSend, sendNow]
  );

  // 他者の最新テキストを、キャレットをできるだけ保ったまま編集領域へ反映する（同時編集の収束時）。
  // markdown モードでは WYSIWYG 再構築、それ以外は textContent 置換。キャレット保存・復元は失敗しても
  // 本文反映は完了させ、キャレット復元だけを諦める（編集不能に陥らせない）。
  const applyRemoteText = useCallback(
    (el: HTMLElement, text: string): void => {
      // 権威 run も同時に反映する（本文だけ合わせると、部分書式が前の本文の位置に残る）。
      const runs = authoritativeInlineStylesRef.current;
      const runsMatch = !sendsInlineStyles || runs === runsRef.current;
      if (readEditor(el) === text && runsMatch) return;
      // 本文を置き換えるため、変換開始時に記録した生オフセットは古くなる（復元に使わない）。
      compositionStartRef.current = null;
      let caret: number | null = null;
      try {
        caret = saveCaret(el);
      } catch {
        caret = null;
      }
      runsRef.current = runs;
      lastSerializedTextRef.current = text;
      writeEditor(el, text, runs);
      if (caret === null) return;
      try {
        restoreCaret(el, Math.min(caret, text.length));
      } catch {
        // 復元に失敗しても本文反映は済んでいるため無視する。
      }
    },
    [readEditor, saveCaret, sendsInlineStyles, writeEditor, restoreCaret]
  );

  // ---- Undo/Redo（markdown）----
  const recordUndoSnapshot = useCallback(
    (text: string, caret: number, kind: "type" | "struct") => {
      if (!isMarkdown || applyingHistoryRef.current) return;
      // 連続タイプは 1 ステップへコアレス（タイプ列の先頭状態だけを積む）。
      if (kind === "type" && lastOpKindRef.current === "type") return;
      // 本文と run を同時に履歴化する（削除した文字の部分書式も Undo で戻せるようにする）。
      undoRef.current.push({ caret, inlineStyles: runsRef.current, text });
      redoRef.current = [];
      lastOpKindRef.current = kind;
    },
    [isMarkdown]
  );

  /**
   * 履歴スナップショットを適用する（本文と run を同時に戻し、同じ 1 通で送信する）。
   * 送信サイズ上限へ収められず適用を見送った場合は `false` を返す（呼び出し側が履歴を戻す）。
   */
  const applyHistorySnapshot = useCallback(
    (el: HTMLElement, snapshot: UndoSnapshot): boolean => {
      // 履歴のスナップショットは文字数上限の内側だが、編集開始時の最初のスナップショットは
      // REST / MCP 経由で作られた「文字数上限内でも WebSocket では送れない」組み合わせであり得る。
      // 追従させる run はスナップショット側（＝ snapshot.text に対応する run）を起点にする。
      const fitted = fitForSend(
        lastSerializedTextRef.current,
        snapshot.text,
        snapshot.caret,
        "struct",
        { forText: snapshot.text, runs: snapshot.inlineStyles }
      );
      // 収められなかった場合は送らず、DOM と run も元のまま残す（サーバ側の本文を壊さない）。
      if (!fitted.shouldSend) return false;
      // 本文を置き換えるため、変換開始時に記録した生オフセットは古くなる（復元に使わない）。
      // なお Undo / Redo は IME 変換中には届かない（onKeyDown・handleBeforeInput とも composing で先に
      // return する）ため、ここは記録のライフサイクルを閉じるための防御的なリセット。
      compositionStartRef.current = null;
      applyingHistoryRef.current = true;
      runsRef.current = fitted.inlineStyles ?? snapshot.inlineStyles;
      lastSerializedTextRef.current = fitted.text;
      buildMarkdownEditorDom(el, fitted.text, runsRef.current);
      restoreEditorCaretOffset(el, fitted.caret);
      applyingHistoryRef.current = false;
      lastOpKindRef.current = "none";
      scheduleSend(fitted.text, sendsInlineStyles ? runsRef.current : undefined);
      onLiveText?.(fitted.text);
      return true;
    },
    [fitForSend, onLiveText, scheduleSend, sendsInlineStyles]
  );

  const performUndo = useCallback(() => {
    if (!isMarkdown) return;
    const el = elementRef.current;
    if (!el || undoRef.current.length === 0) return;
    const now = Date.now();
    // keydown/beforeinput の二重発火防止。同じ種別の連続だけを抑止し、直前が Redo なら通す。
    const last = lastHistoryRef.current;
    if (last.kind === "undo" && now - last.at < HISTORY_DEDUP_MS) return;
    lastHistoryRef.current = { at: now, kind: "undo" };
    const curText = serializeMarkdownEditorDom(el);
    const curCaret = saveEditorCaretOffset(el) ?? curText.length;
    redoRef.current.push({ caret: curCaret, inlineStyles: runsRef.current, text: curText });
    const prev = undoRef.current.pop();
    if (!prev) return;
    if (!applyHistorySnapshot(el, prev)) {
      // 送信サイズ上限へ収められず適用を見送った場合は、取り消しが 1 段消えないよう履歴を戻す。
      undoRef.current.push(prev);
      redoRef.current.pop();
    }
  }, [isMarkdown, elementRef, applyHistorySnapshot]);

  const performRedo = useCallback(() => {
    if (!isMarkdown) return;
    const el = elementRef.current;
    if (!el || redoRef.current.length === 0) return;
    const now = Date.now();
    // 抑止は同じ種別の連続だけ（直前が Undo なら、その直後の Redo は正しい操作として通す）。
    const last = lastHistoryRef.current;
    if (last.kind === "redo" && now - last.at < HISTORY_DEDUP_MS) return;
    lastHistoryRef.current = { at: now, kind: "redo" };
    const curText = serializeMarkdownEditorDom(el);
    const curCaret = saveEditorCaretOffset(el) ?? curText.length;
    undoRef.current.push({ caret: curCaret, inlineStyles: runsRef.current, text: curText });
    const next = redoRef.current.pop();
    if (!next) return;
    if (!applyHistorySnapshot(el, next)) {
      // 送信サイズ上限へ収められず適用を見送った場合は、やり直しが 1 段消えないよう履歴を戻す。
      redoRef.current.push(next);
      undoRef.current.pop();
    }
  }, [isMarkdown, elementRef, applyHistorySnapshot]);

  // 横取りした純粋変換の結果を適用する: Undo 記録 → 正準再構築 → キャレット復元 → 送信。
  // kind="type"（通常入力）は連続を 1 Undo ステップへコアレスし、"struct"（改行・削除・貼付等）は都度記録する。
  const commitTransform = useCallback(
    (
      el: HTMLElement,
      prevText: string,
      prevCaret: number,
      result: EditorTextAndCaret,
      kind: "type" | "struct",
      // 呼び出し側が本文変更と同時に求めた run（と、それが対応する本文）。本文を複数箇所で
      // 書き換える変換（範囲リンク化）では、prevText からの単一置換近似では run を追えないため、
      // 計算済みの run を起点にする。省略時は現在の run（＝ prevText 対応）を使う。
      runsSource?: { readonly runs: readonly TextInlineStyleRange[]; readonly forText: string }
    ) => {
      // 上限へ収めるのは recordUndoSnapshot より前。挿入分が全量切り詰められて本文が変わらない
      // ときに、Undo スナップショットの追加・DOM 再構築・送信予約のいずれも行わずに終えるため。
      // この経路は preventDefault 済みで DOM を自前で組み立てるため、早期 return しても
      // DOM とキャレットは変換前のまま保たれる。
      const fitted = fitForSend(prevText, result.text, result.caret, kind, runsSource);
      if (!fitted.shouldSend) return;
      if (fitted.droppedCount > 0 && fitted.text === prevText) return;
      recordUndoSnapshot(prevText, prevCaret, kind);
      // 本文変更へ追従させた run で DOM を作り直す（同じ op で本文と run を確定させる）。
      if (fitted.inlineStyles !== undefined) runsRef.current = fitted.inlineStyles;
      lastSerializedTextRef.current = fitted.text;
      buildMarkdownEditorDom(el, fitted.text, runsRef.current);
      restoreEditorCaretOffset(el, fitted.caret);
      scheduleSend(fitted.text, fitted.inlineStyles);
      onLiveText?.(fitted.text);
    },
    [fitForSend, recordUndoSnapshot, scheduleSend, onLiveText]
  );

  // ネイティブ委任した入力（通常タイプ・単語削除など）の後始末: 行分類が変わったときだけ正準再構築し、
  // 生テキストを送信する。分類不変なら再構築せずキャレット/IME のネイティブ挙動を温存する。
  // compositionStart を渡すのは IME 変換確定・取り消し（onCompositionEnd）の経路だけ。変換開始時の記録から、
  // 選択が失われていたときの復元先を求める。onInput の経路では渡さないため、選択が無いときの扱いは従来どおり
  // （再構築する分岐は全文末尾へ戻し、再構築しない分岐はキャレットを戻さない）。
  const reconcileAfterNativeInput = useCallback(
    (el: HTMLElement, compositionStart?: CompositionStartRecord | undefined) => {
      const raw = serializeMarkdownEditorDom(el);
      const prevText = lastSerializedTextRef.current;
      const savedCaret = saveEditorCaretOffset(el);
      const fallbackCaret =
        compositionStart === undefined
          ? undefined
          : compositionFallbackCaret(raw, compositionStart);
      // 選択を書き換えてよいのは、編集要素がフォーカスを持つときだけ。再構築の前に 1 回だけ判定し、
      // 再構築する分岐・しない分岐の両方へ同じ判定を適用する。フォーカスが外部へ移っているのに選択を張ると
      // （blur で変換が確定された、Range はエディタ内に残ったままボタンへフォーカスが移った等）、
      // 移動先の選択を奪ったり、ブラウザによってはフォーカスを引き戻したりする。
      const canRestoreCaret = el.ownerDocument.activeElement === el;
      // ネイティブ委任した入力でも本文は変わり得るため、上限へ収めつつ run を追従させる。
      const fitted = fitForSend(prevText, raw, savedCaret ?? fallbackCaret ?? raw.length, "type");
      if (fitted.inlineStyles !== undefined) runsRef.current = fitted.inlineStyles;
      lastSerializedTextRef.current = fitted.text;
      // ブラウザが先に DOM を書き換えている経路のため、収めた本文が DOM と違うなら
      // 本文が変わらない場合でも必ず書き戻す（超過文字が画面に残ると保存本文と乖離する）。
      if (fitted.text !== raw || editorNeedsRebuild(el, fitted.text, fitted.inlineStyles)) {
        buildMarkdownEditorDom(el, fitted.text, runsRef.current);
        if (canRestoreCaret) {
          restoreEditorCaretOffset(el, Math.min(fitted.caret, fitted.text.length));
        }
      } else if (canRestoreCaret && savedCaret === null && fallbackCaret !== undefined) {
        // 本文は正準のままだが選択が失われている（IME 変換を取り消すと、Chrome は空テキストノードの隣が
        // 編集不可のマーカー span のためキャレットを置き直せず、選択を 0 レンジにする）。
        restoreEditorCaretOffset(el, Math.min(fallbackCaret, fitted.text.length));
      }
      if (fitted.shouldSend && fitted.text !== prevText) {
        scheduleSend(fitted.text, fitted.inlineStyles);
      }
      // 切り詰めで本文が変換前と同じに戻った場合、onCompositionStart が積んだ「押しても何も
      // 戻らない Undo」を 1 件だけ取り除く（末尾が現在の本文と一致するときだけ）。
      if (fitted.text === prevText && fitted.droppedCount > 0) {
        const last = undoRef.current[undoRef.current.length - 1];
        if (last && last.text === fitted.text) undoRef.current.pop();
      }
      onLiveText?.(fitted.text);
    },
    [fitForSend, scheduleSend, onLiveText]
  );

  // 非 Markdown（セクション名など）の入力後始末。既定の貼り付け・入力をブラウザへ委ねているため、
  // 事前に横取りせず、入力後に本文を読み直して上限へ収める（ネイティブの Undo スタックを壊さない）。
  const reconcilePlainInput = useCallback(
    (el: HTMLElement) => {
      const raw = readEditorText(el);
      const caret = saveCaretCharacterOffset(el);
      const fitted = fitForSend(lastSerializedTextRef.current, raw, caret ?? raw.length, "type");
      // 切り詰めが起きない通常入力では DOM へ書き戻さない（IME・キャレットのネイティブ挙動を温存）。
      if (fitted.text !== raw) {
        el.textContent = fitted.text;
        restoreCaretCharacterOffset(el, fitted.caret);
      }
      lastSerializedTextRef.current = fitted.text;
      if (fitted.shouldSend) scheduleSend(fitted.text);
      // 付箋など、入力中も表示（フォントサイズ）を本文へ追従させたい種別へ通知する。
      onLiveText?.(fitted.text);
    },
    [fitForSend, scheduleSend, onLiveText]
  );

  // 編集モードに入った直後は要素にフォーカスし、初期テキストを反映する。
  //
  // 競合防止: editing 中に他者編集や自分の debounce echo で `initialText` が変動しても
  // ローカル入力を上書きしないよう、最新値は ref に保持して effect の依存からは外す。
  // 結果として「isEditing が false→true へ遷移した瞬間」だけ初期化され、編集中の
  // 外部更新は無視される（他者の最新値は editing 終了後の再レンダーで反映される）。
  const initialTextRef = useRef(initialText);
  initialTextRef.current = initialText;
  useEffect(() => {
    if (!isEditing) return;
    const el = elementRef.current;
    if (!el) return;
    const initial = initialTextRef.current;
    // 編集開始時は権威 run を起点にする（前回の編集セッションのローカル run を持ち越さない）。
    runsRef.current = authoritativeInlineStylesRef.current;
    lastSerializedTextRef.current = initial;
    if (isMarkdown) {
      // markdown は空本文でも必ず正準ブロック（空なら blank ブロック）を構築する。
      // readEditor(空 el)==="" となり initial==="" と一致してしまうと build がスキップされ、
      // ブロックが 1 つも無い contentEditable になって初回入力が裸ノード化する不具合を防ぐ。
      writeEditor(el, initial, runsRef.current);
    } else if (readEditor(el) !== initial) {
      writeEditor(el, initial);
    }
    // 編集開始時点のテキストを「同期済み」の基準にする（この後の収束判定の起点）。
    lastSyncedTextRef.current = initial;
    pendingSendRef.current = null;
    // Undo/Redo スタックを編集開始時にクリアする（markdown のみ使用）。
    undoRef.current = [];
    redoRef.current = [];
    lastOpKindRef.current = "none";
    // 前回の編集セッションの範囲書式通知を持ち越さない（持ち越すと、編集開始直後の
    // 権威 run 反映で不要なスナップショットを 1 件積んでしまう）。
    lastRangeStyleSignalRef.current = rangeStyleAppliedSignalRef.current;
    // 切り詰めのお知らせの間引きは編集セッション単位で数え直す。
    lastFitNoticeAtRef.current = 0;
    // 前回の編集セッションの keydown 記録を持ち越さない（markdown のみ使用）。
    lastKeydownWasImeRef.current = false;
    // 前回の編集セッションの変換開始記録も持ち越さない（compositionend が届かないまま終了した場合）。
    compositionStartRef.current = null;
    // 前回の編集セッションの中継記録も持ち越さない。持ち越すと、新しい編集の最初の
    // 「エディタ外へのドラッグ」で、消すべき帯が無いのに解除通知が 1 通飛ぶ。
    lastSentRef.current = null;
    el.focus();
    if (isMarkdown) {
      // キャレットを本文末尾へ（正準ブロック内のテキストノードに置く）。
      // el 直下（ブロックの外）に置くと、最初のキー入力がブロック外の裸テキストノードとして挿入され、
      // serialize（直下の要素ブロックのみ走査）に取りこぼされて 1 文字目が消える不具合になる。
      restoreEditorCaretOffset(el, initial.length);
    } else {
      // キャレットを末尾に
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [isEditing, elementRef, readEditor, writeEditor, isMarkdown]);

  // 同時編集の収束（エピックボードと同じ「全文 LWW リレー + idle 時のみ適用」の設計を流用）。
  // 編集中に他者やサーバの確定で initialText（= object.text の権威値）が変化したとき、ローカルに
  // 未送信の変更が無ければ（＝現在の DOM が最後に同期した内容と一致していれば）、その最新テキストを
  // キャレットを保ったまま反映する。未送信変更がある間は反映せず、自分の debounce 送信が
  // 全文 LWW で勝つ（結果として双方が最終的に同じ内容へ収束する）。
  // これにより「同じテキスト図形を複数人が同時に開いて編集し、互いの入力が反映される」体験になる。
  useEffect(() => {
    if (!isEditing) return;
    const el = elementRef.current;
    if (!el) return;
    // 自分の送信エコーを含め、同期済みと同じなら何もしない。
    if (initialText === lastSyncedTextRef.current) return;
    // 未送信の debounce 送信が保留中の間は「idle でない」とみなし、リモートを適用しない。
    // DOM 上は一時的に同期済みと一致していても（入力してすぐ元へ戻した直後など）、保留中の timer が
    // 後から古いテキストを送り返して、先に適用したリモート更新を巻き戻してしまうため。
    // timer が確定（発火）して完全に idle になってから、次のリモート更新で適用する。
    if (timerRef.current !== null) return;
    // ローカルに未送信の変更がある（diverged）間は上書きしない（自分の送信が LWW で反映される）。
    if (readEditor(el) !== lastSyncedTextRef.current) return;
    applyRemoteText(el, initialText);
    lastSyncedTextRef.current = initialText;
    // リモート適用後は自前 Undo/Redo をリセットする（Undo が旧ローカル状態へ戻り、
    // その差分が LWW で相手の変更を上書きしてしまうのを防ぐ）。
    undoRef.current = [];
    redoRef.current = [];
    lastOpKindRef.current = "none";
  }, [isEditing, initialText, elementRef, readEditor, applyRemoteText]);

  // 送信サイズの事前判定に使う封筒サイズは、対象・編集セッションごとに 1 回だけ実測する
  // （実測は fitForSend の初回呼び出しで遅延して行う）。対象が変わったら測り直す。
  useEffect(() => {
    envelopeBytesRef.current = null;
  }, [isEditing, objectId, dispatcher]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const enterEditMode = useCallback(() => {
    if (isDisabled) return;
    isEditingRef.current = true;
    setEditing(true);
  }, [isDisabled]);
  const leaveEditMode = useCallback(() => {
    // 編集中でなければ何もしない（最重要ガード）。
    // 非編集時の本文は React 管理の <a> / <span>（LinkifiedText）で描画されており、リンクは
    // フォーカスを持てるため、リンククリック（新規タブで開く）や Escape で、編集していないのに
    // onBlur / onKeyDown 経由で本関数へ到達し得る。そのまま進むと下の textContent = "" が
    // React 管理下の子ノードを消して「本文表示が消える」「次の reconcile で removeChild が
    // NotFoundError で落ちてエラー画面になる」という不具合になる（v0.2.79 で顕在化した実障害）。
    if (!isEditingRef.current) return;
    isEditingRef.current = false;
    // 変換中に編集を終えた場合の記録を残さない。
    compositionStartRef.current = null;
    const el = elementRef.current;
    const text = el ? readEditor(el) : "";
    // 編集終了時に、contentEditable 中にブラウザが作った DOM ノード（テキスト・改行の <div>/<br>）を
    // 明示的に消してから非編集表示へ戻す。非編集表示はテキストを <span>/<a>（要素）で描画するため、
    // React は「以前の子＝null」からの差分で要素を "追記" するだけで、この未追跡ノードを消さない。
    // 結果として旧テキストと新表示が二重化する（要素の子は単一テキスト子のような textContent 置換に
    // ならないため）。ここで空にしておけば、React は空の要素へ非編集表示を入れるだけで二重化しない。
    if (el) el.textContent = "";
    setEditing(false);
    // 0 文字（空白のみを含む）で確定した場合、テキスト図形はキャンバス上で見えなくなり
    // 位置も分からなくなるため、対象コールバック（テキスト図形の自動削除）へ委ねる。
    // このとき空の text.update は送らず、保留中の送信も破棄する。
    if (onEmptyCommit && text.trim().length === 0) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingSendRef.current = null;
      onEmptyCommit();
      return;
    }
    if (el) {
      const prevText = lastSerializedTextRef.current;
      if (text === prevText) {
        // 編集中に本文が変わっていない（開いて閉じただけ、または直前の入力で既に上限へ
        // 収めてある）。ここで収め直すと、REST / MCP 由来や書式 op の積み重ねで
        // もともと送信サイズ上限を超えている本文を、開閉しただけで削ってしまう。
        // 保留分をそのまま確定させる（内容が同じなら dispatcher の no-op 判定で送られない）。
        flushPending(text, sendsInlineStyles ? runsRef.current : undefined);
      } else {
        // 合流点を通らずに DOM が変わった経路。確定送信もフックの送信経路のひとつなので、
        // ここでも上限へ収める。DOM はこの直後に空へ戻すため、再構築は不要。
        const fitted = fitForSend(prevText, text, text.length, "struct");
        if (fitted.shouldSend) {
          flushPending(fitted.text, fitted.inlineStyles);
        } else {
          // 送っても送信サイズ上限で拒否されるだけなので、保留中の送信も破棄する。
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
          pendingSendRef.current = null;
        }
      }
    }
  }, [elementRef, fitForSend, flushPending, onEmptyCommit, readEditor, sendsInlineStyles]);

  const onInput = useCallback(
    (event: React.FormEvent<HTMLElement>) => {
      if (composingRef.current) return;
      const el = event.currentTarget as HTMLElement;
      if (isMarkdown) {
        // 構造変更は beforeinput で横取り済み。ここへ来るのはネイティブ委任した通常入力の後始末。
        reconcileAfterNativeInput(el);
        return;
      }
      reconcilePlainInput(el);
    },
    [isMarkdown, reconcileAfterNativeInput, reconcilePlainInput]
  );

  const onCompositionStart = useCallback(() => {
    composingRef.current = true;
    // 記録は必ず代入し直す（前の変換の記録を持ち越さない）。取れなかった場合は null のまま。
    compositionStartRef.current = null;
    if (!isMarkdown) return;
    // IME 変換開始時点（変換前）の状態を Undo の起点として記録する。
    const el = elementRef.current;
    if (!el) return;
    const sel = saveEditorSelection(el);
    const raw = serializeMarkdownEditorDom(el);
    // 変換取り消し時のキャレット復元用。選択が取れないときは記録しない（＝復元しない）。
    compositionStartRef.current =
      sel === null ? null : { end: sel.end, rawLength: raw.length, start: sel.start };
    recordUndoSnapshot(raw, sel?.start ?? 0, "type");
  }, [isMarkdown, elementRef, recordUndoSnapshot]);

  const onCompositionEnd = useCallback(
    (event: React.CompositionEvent<HTMLElement>) => {
      composingRef.current = false;
      compositionEndedAtRef.current = Date.now();
      const el = event.currentTarget as HTMLElement;
      if (isMarkdown) {
        // 変換開始時の記録は、使ったかどうかに関わらずここで消費する（次の変換へ持ち越さない）。
        const compositionStart = compositionStartRef.current;
        compositionStartRef.current = null;
        // IME 変換確定後に整形を反映する（変換中は composing ガードで再構築しない）。
        // 取り消しで選択が失われていた場合は、変換開始位置へキャレットを戻す。
        reconcileAfterNativeInput(el, compositionStart ?? undefined);
        return;
      }
      // IME 変換確定時にも最新本文で反映・通知する（変換中は onInput の composing ガードで呼ばれない）。
      reconcilePlainInput(el);
    },
    [isMarkdown, reconcileAfterNativeInput, reconcilePlainInput]
  );

  // ---- テキスト範囲選択（範囲専用書式パネルの表示条件）----
  // 自分の objectId ぶんだけを購読する。範囲を扱わない種別（付箋・セクション等）では常に null。
  const activeRange = useCanvasStore((s) =>
    isRangeStyleEnabled && s.activeTextRangeSelection?.objectId === objectId
      ? s.activeTextRangeSelection
      : null
  );
  const panelInteracting = activeRange?.panelInteracting ?? false;
  const panelInteractingRef = useRef(false);
  panelInteractingRef.current = panelInteracting;
  // Escape のようなイベントハンドラから最新の範囲を参照するための ref（依存を増やさない）。
  const activeRangeRef = useRef(activeRange);
  activeRangeRef.current = activeRange;

  /** 現在の DOM 選択から範囲状態を更新する（対象外なら解除）。 */
  const syncRangeSelection = useCallback(() => {
    if (!isRangeStyleEnabled) return;
    const el = elementRef.current;
    if (!el || !isEditingRef.current || isDisabled) {
      setActiveTextRangeSelection(null);
      return;
    }
    // パネル操作中は DOM 選択が一時的に外れても最後の範囲を保持する。
    const clearUnlessInteracting = (): void => {
      if (panelInteractingRef.current) return;
      setActiveTextRangeSelection(null);
    };
    // 枠外へのドラッグ中にブラウザーが広げた選択を、読む前に保存値へ戻す（範囲パネルへ誤った範囲を出さない）。
    reconcileSelection();
    const view = el.ownerDocument.defaultView;
    const selection = view?.getSelection?.() ?? null;
    if (!selection || selection.rangeCount === 0) {
      clearUnlessInteracting();
      return;
    }
    const domRange = selection.getRangeAt(0);
    // 始点・終点のどちらかが編集要素の外（他要素へまたがる選択）ならパネルを出さない。
    if (!el.contains(domRange.startContainer) || !el.contains(domRange.endContainer)) {
      clearUnlessInteracting();
      return;
    }
    const raw = rawSelectionFromRange(el, domRange);
    if (!raw || raw.start === raw.end) {
      clearUnlessInteracting();
      return;
    }
    const rawText = serializeMarkdownEditorDom(el);
    // 保存済み本文と編集 DOM の本文が食い違っている間はパネルを出さない。
    // 範囲書式 op は「保存済み本文（expectedText）+ raw offset」で送るため、DOM だけが先に
    // 進んでいる状態（debounce 待ち・未 ack で送信保留）や、保存本文が CRLF で DOM が LF へ
    // 正規化されている状態で適用すると、別の文字へ書式が付く。保存本文が追いついた時点で
    // 下の effect が再判定してパネルを出し直す。
    //
    // 自分の本文更新が未 ack のあいだも出さない。op ごとに別 Lambda で処理されるため、
    // 本文更新より先に書式 op が処理されると expectedText 不一致で無駄に競合 reject になる。
    if (
      sendsInlineStyles &&
      (rawText !== lastSyncedTextRef.current || hasInFlightTextUpdateRef.current)
    ) {
      clearUnlessInteracting();
      return;
    }
    // Markdown マーカーだけを含む選択（可視本文が 0 文字）は no-op としてパネルを出さない。
    const visible = clipRangeToMarkdownVisible(buildMarkdownSourceMap(rawText), raw.start, raw.end);
    if (visible.length === 0) {
      clearUnlessInteracting();
      return;
    }
    const anchorRect = unionSelectionRect(domRange);
    if (!anchorRect) {
      // 矩形が取得できない場合はパネルを配置できないため閉じる。
      setActiveTextRangeSelection(null);
      return;
    }
    setActiveTextRangeSelection({
      objectId,
      start: raw.start,
      end: raw.end,
      text: rawText,
      anchorRect,
      panelInteracting: false,
    });
  }, [
    isRangeStyleEnabled,
    elementRef,
    isDisabled,
    objectId,
    sendsInlineStyles,
    setActiveTextRangeSelection,
    reconcileSelection,
  ]);

  const onBlur = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      // 範囲パネルの操作でフォーカスが移った場合は編集を続ける（操作確定後に選択を復元する）。
      if (panelInteractingRef.current) return;
      if (
        event.relatedTarget instanceof Element &&
        event.relatedTarget.closest(`[${TEXT_RANGE_PANEL_ATTRIBUTE}="true"]`) !== null
      ) {
        return;
      }
      leaveEditMode();
    },
    [leaveEditMode]
  );

  // 編集中の自分のキャレット位置（本文先頭からの文字オフセット）と、範囲選択中はその終端も
  // 他参加者へ中継する。saveCaretCharacterOffset は innerText 基準（改行 "\n" を含む）で
  // オフセットを求めるため、受信側（object.text を描画した DOM）で同じ文字数の位置に Range を
  // 作れば位置が一致する。markdown モードでは saveEditorSelection（生テキスト基準）を用いる
  // （従来と同じ生オフセット座標系で、その start は saveEditorCaretOffset と同一値）。
  const caretThrottleRef = useRef<{
    lastAt: number;
    lastSentAt: number;
    timer: ReturnType<typeof setTimeout> | null;
  }>({ lastAt: 0, lastSentAt: 0, timer: null });
  // 中継用の選択範囲を取得する。markdown（付箋・テキスト図形）は生テキスト基準の
  // { start, end } を返す saveEditorSelection を使う。非 markdown（セクション等）は
  // 選択範囲を共有しないため、キャレット位置を collapsed な範囲として返す
  // （リモートキャレットレイヤの描画対象が text / sticky のみであることと対応する）。
  const saveSelectionForBroadcast = useCallback(
    (el: HTMLElement): EditorSelection | null => {
      if (isMarkdown) return saveEditorSelection(el);
      const offset = saveCaretCharacterOffset(el);
      return offset === null ? null : { end: offset, start: offset };
    },
    [isMarkdown]
  );
  const broadcastCaretNow = useCallback(
    (options?: { readonly keepalive?: boolean | undefined } | undefined) => {
      if (isDisabled) return;
      if (composingRef.current) return; // IME 変換中は送らない（確定時の selectionchange で送る）
      const el = elementRef.current;
      if (!el) return;
      // 末尾送信の予約やハートビートは selectionchange と独立に走るため、ここでも読む前に
      // 枠外のドラッグで広がった選択を保存値へ戻す（他参加者へ誤った範囲の帯を送らない）。
      reconcileSelection();
      const selection = saveSelectionForBroadcast(el);
      if (!selection) {
        // 始点はエディタ内だが終点が外にある（エディタから外へドラッグした）場合、
        // saveEditorSelection は null を返す。ここで何も送らないと、他参加者の画面に
        // 最後に送った帯が最大約 2.5 秒残り、選択していない範囲が塗られたままになる。
        //
        // ただし selectionchange はエディタ外での選択操作中も繰り返し発火するため、
        // 毎回送ると通数が増える。直前が範囲選択中であった場合に限り「終端なし」の
        // 1 通を送って帯だけを消す（キャレットは直前の位置に残す）。
        const last = lastSentRef.current;
        // selectionchange 由来では帯を消す 1 通だけを送り、以後は黙る。ハートビート由来では
        // 選択端が外にある間も、最後のキャレット位置だけは保持する。
        if (last !== null && (last.hadSelection || options?.keepalive === true)) {
          dispatcher.previewTextCursor(objectId, last.caretIndex, undefined);
          const sentAt = Date.now();
          caretThrottleRef.current.lastSentAt = sentAt;
          caretThrottleRef.current.lastAt = sentAt;
          lastSentRef.current = { caretIndex: last.caretIndex, hadSelection: false };
        }
        return;
      }
      const hadSelection = selection.end > selection.start;
      lastSentRef.current = { caretIndex: selection.start, hadSelection };
      dispatcher.previewTextCursor(
        objectId,
        selection.start,
        hadSelection ? selection.end : undefined
      );
      const sentAt = Date.now();
      caretThrottleRef.current.lastSentAt = sentAt;
      caretThrottleRef.current.lastAt = sentAt;
    },
    [isDisabled, elementRef, dispatcher, objectId, saveSelectionForBroadcast, reconcileSelection]
  );
  const scheduleBroadcastCaret = useCallback(() => {
    const state = caretThrottleRef.current;
    const now = Date.now();
    const elapsed = now - state.lastAt;
    if (elapsed >= CARET_BROADCAST_INTERVAL_MS) {
      state.lastAt = now;
      broadcastCaretNow();
      return;
    }
    if (state.timer) return; // 末尾送信を既に予約済み
    state.timer = setTimeout(() => {
      state.timer = null;
      caretThrottleRef.current.lastAt = Date.now();
      broadcastCaretNow();
    }, CARET_BROADCAST_INTERVAL_MS - elapsed);
  }, [broadcastCaretNow]);

  // 編集中は selectionchange を購読し、キャレット / 選択が自分の編集要素内で動いたら位置を中継する。
  // タイプ・矢印キー・クリックのいずれでもキャレット移動時に発火するため、これ 1 つで網羅できる。
  useEffect(() => {
    if (!isEditing) return undefined;
    const onSelectionChange = (): void => {
      // 文字選択のドラッグが編集面の外にある間にブラウザーが選択を広げていたら、先に保存値へ戻す
      // （戻した選択だけを範囲パネルと他参加者への中継が読む。Issue #160）。
      reconcileSelection();
      // 範囲パネルの表示条件は、選択が編集要素の外へ出た場合の解除も含めて毎回判定する。
      syncRangeSelection();
      const el = elementRef.current;
      const selection = window.getSelection();
      if (!el || !selection || selection.rangeCount === 0) return;
      if (!el.contains(selection.getRangeAt(0).startContainer)) return;
      scheduleBroadcastCaret();
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange);
      const throttle = caretThrottleRef.current;
      if (throttle.timer) {
        clearTimeout(throttle.timer);
        throttle.timer = null;
      }
    };
  }, [isEditing, elementRef, scheduleBroadcastCaret, syncRangeSelection, reconcileSelection]);

  // 編集の終了・ロック・参照専用化で範囲パネルを閉じる。
  useEffect(() => {
    if (!isRangeStyleEnabled) return;
    if (isEditing && !isDisabled) return;
    setActiveTextRangeSelection(null);
  }, [isRangeStyleEnabled, isEditing, isDisabled, setActiveTextRangeSelection]);

  // 保存済み本文が自分の送信で追いつき、未 ack の本文更新も無くなったら、範囲パネルの
  // 表示条件を再判定する（送信中のあいだ syncRangeSelection は範囲を出さないため）。
  useEffect(() => {
    if (!isRangeStyleEnabled || !isEditing) return;
    syncRangeSelection();
  }, [isRangeStyleEnabled, isEditing, initialText, hasInFlightTextUpdate, syncRangeSelection]);

  // 対象がアンマウントされたとき（他参加者の削除・ゴミ箱化など）にも範囲を解除する。
  // 上の effect はアンマウント時に走らないため、残したままだと「対象が存在しない範囲」が
  // state に居座り、全文用パネルの抑止が解けなくなる。
  useEffect(() => {
    if (!isRangeStyleEnabled) return undefined;
    return () => {
      const store = useCanvasStore.getState();
      if (store.activeTextRangeSelection?.objectId !== objectId) return;
      store.setActiveTextRangeSelection(null);
    };
  }, [isRangeStyleEnabled, objectId]);

  // 権威 run が変わったら編集 DOM を作り直す（自分の範囲操作・他参加者の書式変更の両方）。
  // 未送信のローカル本文変更がある間は当てず、自分の送信で収束させる（本文 LWW と同じ方針）。
  const inlineStylesProp = options.inlineStyles;
  useEffect(() => {
    if (!isEditing || !isMarkdown || !sendsInlineStyles) return;
    const el = elementRef.current;
    if (!el) return;
    const runs = inlineStylesProp ?? [];
    if (runs === runsRef.current) {
      // run が変わっていなければ履歴へ積むものも無い。通知だけ消費して、この後の他参加者の
      // 書式変更を「自分の操作」と誤認して積まないようにする。
      lastRangeStyleSignalRef.current = rangeStyleAppliedSignal;
      return;
    }
    if (timerRef.current !== null) return;
    const raw = serializeMarkdownEditorDom(el);
    if (raw !== lastSyncedTextRef.current) return;
    const previous = saveEditorSelection(el);
    // 自分の範囲書式操作による run 変化なら、変更前の run を編集中の Undo 履歴へ積む。
    // 編集中の Ctrl+Z は自前のスナップショットスタックが正本のため、積まないと範囲書式が
    // 取り消せず、直前の入力の取り消しへ消費される。他参加者の書式変更では積まない
    // （他者の変更を自分の Undo で巻き戻さないため、run の変化だけを合図にはしない）。
    if (lastRangeStyleSignalRef.current !== rangeStyleAppliedSignal) {
      lastRangeStyleSignalRef.current = rangeStyleAppliedSignal;
      recordUndoSnapshot(raw, previous?.start ?? 0, "struct");
    }
    runsRef.current = runs;
    buildMarkdownEditorDom(el, raw, runs);
    if (previous) restoreEditorSelection(el, previous.start, previous.end);
  }, [
    isEditing,
    isMarkdown,
    sendsInlineStyles,
    inlineStylesProp,
    elementRef,
    rangeStyleAppliedSignal,
    recordUndoSnapshot,
  ]);

  // パネル操作が終わったら、エディタへフォーカスと選択範囲を戻す（続けて別項目を操作できるようにする）。
  const wasPanelInteractingRef = useRef(false);
  useEffect(() => {
    const wasInteracting = wasPanelInteractingRef.current;
    wasPanelInteractingRef.current = panelInteracting;
    if (!isEditing || panelInteracting || !wasInteracting) return;
    if (!activeRange) return;
    const el = elementRef.current;
    if (!el) return;
    el.focus();
    restoreEditorSelection(el, activeRange.start, activeRange.end);
  }, [panelInteracting, isEditing, activeRange, elementRef]);

  // タイマーの依存から broadcastCaretNow を外す。参照が変わるたびに interval を張り直すと、
  // 短い再レンダーが続く間にハートビートが一度も発火しなくなるためである。
  const broadcastCaretNowRef = useRef(broadcastCaretNow);
  useEffect(() => {
    broadcastCaretNowRef.current = broadcastCaretNow;
  }, [broadcastCaretNow]);

  // ブラウザは選択が動かない限り selectionchange を発火しない。付箋・テキスト図形の編集中は
  // 同じキャレットと選択範囲を送り直し、受信側の掃除で表示が消えないようにする。
  useEffect(() => {
    if (!isEditing || isDisabled || isTrashed || !isMarkdown) return undefined;
    // 前回の編集セッションの実送信時刻を持ち越さず、最初の tick で現在位置を送る。
    caretThrottleRef.current.lastSentAt = 0;
    const id = window.setInterval(() => {
      // 最新の選択を送る既存の末尾送信を優先し、二重送信を避ける。
      if (caretThrottleRef.current.timer !== null) return;
      if (Date.now() - caretThrottleRef.current.lastSentAt < CARET_KEEPALIVE_INTERVAL_MS) return;
      broadcastCaretNowRef.current({ keepalive: true });
    }, CARET_KEEPALIVE_TICK_MS);
    return () => window.clearInterval(id);
  }, [isEditing, isDisabled, isTrashed, isMarkdown]);

  // 構造変更（Enter・改行・削除・履歴）を beforeinput で一元横取りする（markdown のみ）。
  // React の onBeforeInput は inputType の扱いにばらつきがあるため、ネイティブリスナで確実に受ける。
  const handleBeforeInput = useCallback(
    (event: Event) => {
      if (!isMarkdown) return;
      const e = event as InputEvent;
      const el = elementRef.current;
      if (!el) return;
      const type = e.inputType;
      if (type === "insertParagraph" || type === "insertLineBreak") {
        // IME 変換中の改行入力は「変換確定の Enter」であり改行意図ではないため、ネイティブに
        // 委任せず破棄する。委任するとブラウザのブロック分割が bullet div を data-prefix ごと
        // クローンし（マーカー span はキャレット前方のため複製されない）、直後の serialize が
        // 幻の空箇条書きを具現化して「確定 Enter だけでリスト継続」になってしまう。
        // ※ cancelable=false の環境では preventDefault は no-op だが、その場合は serialize 側の
        //    正準構造チェック（span を欠く bullet の data-prefix を信用しない）が受け皿になる。
        if (composingRef.current || e.isComposing) {
          lastKeydownWasImeRef.current = false;
          e.preventDefault();
          return;
        }
        // 変換確定の Enter が isComposing=false で漏れてきた場合の識別。直前の keydown が
        // IME 変換中の発火（Windows Chrome では key="Process"）なら、この改行入力は確定の漏れで
        // あり、着弾が確定処理の遅延でどれだけ遅れても破棄する。破棄はどの分岐でも one-shot で
        // 記録を消費し、keydown を伴わない改行入力（音声入力・仮想キーボード等）を stale な記録で
        // 恒常的に破棄しない。
        if (lastKeydownWasImeRef.current) {
          lastKeydownWasImeRef.current = false;
          e.preventDefault();
          return;
        }
        // Safari 型の漏れ（compositionend 後の keydown 自体が本物の Enter と区別できない）向けの
        // 既存ガード。確定直後 50ms 以内の本物 Enter も巻き込む既知のトレードオフは受容する
        // （改善余地: この壁時計窓は Safari 型経路の受け皿でしかないため、将来 UA/挙動判定で
        //   Safari 系に限定すれば他ブラウザの取りこぼしを無くせる）。
        if (Date.now() - compositionEndedAtRef.current < COMPOSITION_ENTER_GUARD_MS) {
          lastKeydownWasImeRef.current = false;
          e.preventDefault();
          return;
        }
      }
      if (composingRef.current || e.isComposing) return; // IME 中は（改行以外を）ネイティブに委ねる
      // 書式系の inputType（ブラウザのメニュー等から届く既定の装飾）は、生テキストの正準化として
      // 処理しても選択が畳まれるだけで意味が無い。太字は onKeyDown で範囲書式へ割り当てているため、
      // ここでは何もせず既定動作だけを止める（入口を keydown 1 つに保ち、二重適用を防ぐ）。
      //
      // この分岐は rangeStyle の有無で分けない（markdown を使う種別すべてに効かせる）。
      // onKeyDown 側の Ctrl+B は、範囲書式を持たない種別（付箋）では preventDefault せずに
      // 抜けるため、付箋ではブラウザ既定の formatBold がここへ届く。届いたものを素通しすると、
      // ブラウザが編集 DOM へ <b> や font-weight を差し込み、serializeMarkdownEditorDom が
      // 前提とする正準ブロック構造を壊す。下の既定分岐（transformForInput の default）へ
      // 落としても、生テキストの正準化として DOM を組み直すだけで選択が畳まれる。
      // どちらも「何も起きない」にならないため、ここで止めて何もしないのが最小の防御になる。
      if (
        type === "formatBold" ||
        type === "formatItalic" ||
        type === "formatUnderline" ||
        type === "formatStrikeThrough"
      ) {
        e.preventDefault();
        return;
      }
      if (type === "historyUndo") {
        e.preventDefault();
        performUndo();
        return;
      }
      if (type === "historyRedo") {
        e.preventDefault();
        performRedo();
        return;
      }

      const targetRanges = typeof e.getTargetRanges === "function" ? e.getTargetRanges() : [];
      const firstRange = targetRanges[0];
      const collapsedSingle = targetRanges.length === 1 && !!firstRange && firstRange.collapsed;

      const raw = serializeMarkdownEditorDom(el);
      // オートコレクト（insertReplacementText）は見た目の Selection が collapsed でも対象範囲が語になる
      // ため、対象 Range を優先して生オフセットへ変換する。
      const sel = firstRange
        ? (rawSelectionFromRange(el, firstRange) ?? saveEditorSelection(el))
        : saveEditorSelection(el);

      // 削除をネイティブに委任してよいのは「対象が本文テキストノード内・単一 collapsed・境界でない」場合のみ。
      // 空 blank ブロック（<br>）等の要素上の collapsed でネイティブ削除させると <br> が消えて空行高さ・
      // キャレットアンカーが壊れるため、テキストノード上でない削除は横取りして正準再構築で扱う。
      const deleteInTextNode =
        collapsedSingle && firstRange?.startContainer.nodeType === Node.TEXT_NODE;
      if (
        deleteInTextNode &&
        sel &&
        (type === "deleteContentBackward" || type === "deleteWordBackward") &&
        !isBackspaceBoundary(raw, sel.start)
      ) {
        recordUndoSnapshot(raw, sel.start, "type");
        return;
      }
      if (
        deleteInTextNode &&
        sel &&
        (type === "deleteContentForward" || type === "deleteWordForward") &&
        !isDeleteForwardBoundary(raw, sel.start)
      ) {
        recordUndoSnapshot(raw, sel.start, "type");
        return;
      }

      // それ以外（通常入力 insertText・改行・境界削除・範囲削除など）は横取りして純粋変換で適用する。
      // 通常入力もネイティブに委ねると空ブロックへの初回入力で裸テキストノードが生じ 1 文字目を
      // 取りこぼす（contentEditable 既知の不安定挙動）ため、入力もこちらで確実に制御する。
      e.preventDefault();
      if (!sel) return;
      const result = transformForInput(type, raw, sel, e.data);
      if (!result) return;
      const kind: "type" | "struct" =
        type === "insertText" ||
        type === "insertReplacementText" ||
        type === "insertFromComposition"
          ? "type"
          : "struct";
      commitTransform(el, raw, sel.start, result, kind);
    },
    [isMarkdown, elementRef, performUndo, performRedo, recordUndoSnapshot, commitTransform]
  );

  useEffect(() => {
    if (!isMarkdown || !isEditing) return undefined;
    const el = elementRef.current;
    if (!el) return undefined;
    el.addEventListener("beforeinput", handleBeforeInput);
    return () => el.removeEventListener("beforeinput", handleBeforeInput);
  }, [isMarkdown, isEditing, elementRef, handleBeforeInput]);

  // 貼り付け:
  // - preserveHtmlAnchorsOnPaste 有効時は、リンク付き HTML を [表示テキスト](URL) へ変換して取り込む。
  // - markdown 編集では挿入を自前制御する（裸ノード化を避ける）。HTML アンカーがあれば変換結果を、
  //   無ければ text/plain を選択範囲へ挿入する。
  // - 非 markdown（セクション等）でも opt-in 時は範囲選択へ URL をリンク化する。それ以外は、
  //   従来どおり HTML アンカーを変換できた場合だけ挿入し、通常の貼り付けは既定へ任せる。
  const onPaste = useCallback(
    (event: React.ClipboardEvent<HTMLElement>) => {
      if (!isEditing) return;
      const el = elementRef.current;
      if (!el) return;
      const converted = preserveHtmlAnchorsOnPaste
        ? htmlFragmentToLinkText(event.clipboardData.getData("text/html"))
        : undefined;

      if (isMarkdown) {
        event.preventDefault();
        const plain = event.clipboardData.getData("text/plain");
        const text = converted ?? plain;
        const sel = saveEditorSelection(el);
        if (!sel) return;
        const raw = serializeMarkdownEditorDom(el);

        // 範囲選択 × 単一 URL の貼り付けは、選択部分を [表示テキスト](URL) へ変換する。
        // 判定に使うのは text/plain である（リンク付き HTML をコピーした場合でも、text/plain が
        // 単一 URL なら「URL を貼り付けた」と解釈する）。
        // 変換できない場合（可視文字が無い / 上限超過 / URL 不正）は、従来どおりの挿入へ落とす。
        if (linkifySelectionOnUrlPaste && sel.end > sel.start && isLikelyPasteUrl(plain)) {
          const outcome = linkifyMarkdownRange({
            base: baseStyleRef.current,
            end: sel.end,
            maxLength: TEXT_BODY_MAX_LENGTH,
            runs: runsRef.current,
            start: sel.start,
            text: raw,
            url: plain.trim(),
          });
          if (outcome.kind === "linkified") {
            // 本文を複数箇所で書き換えるため、追従済みの run を起点として渡す
            //（prevText からの単一置換近似では run を追えない）。
            commitTransform(
              el,
              raw,
              sel.start,
              { caret: outcome.caret, text: outcome.text },
              "struct",
              { forText: outcome.text, runs: outcome.runs }
            );
            return;
          }
          // すでに同じ URL のリンクになっている場合は、本文も DOM もキャレットも変えない。
          if (outcome.kind === "unchanged") return;
          if (outcome.kind === "too-long") {
            showToast("warning", "本文が長すぎるため、URL をそのまま貼り付けました。");
          }
        }

        commitTransform(el, raw, sel.start, insertTextAt(raw, sel.start, sel.end, text), "struct");
        return;
      }

      // 非 markdown: 現在の選択範囲だけを置換して、ブラウザのネイティブ Undo を保つ。
      if (linkifySelectionOnUrlPaste) {
        const plain = event.clipboardData.getData("text/plain");
        if (isLikelyPasteUrl(plain)) {
          const outcome = tryLinkifyPlainSelection(el, plain.trim());
          if (outcome === "linkified") {
            event.preventDefault();
            return;
          }
          if (outcome === "too-long") {
            event.preventDefault();
            showToast("warning", "本文が長すぎるため、URL をそのまま貼り付けました。");
            insertPlainTextAtSelection(el, plain.trim());
            return;
          }
        }
      }

      // アンカーを含む HTML のときだけ変換して挿入（それ以外は既定の貼り付けに任せる）。
      if (converted === undefined) return;
      event.preventDefault();
      insertPlainTextAtSelection(el, converted);
    },
    [
      isEditing,
      isMarkdown,
      elementRef,
      preserveHtmlAnchorsOnPaste,
      linkifySelectionOnUrlPaste,
      commitTransform,
      showToast,
    ]
  );

  // カット: 選択文字列を clipboard へ書いてから（①）純粋変換で範囲削除する（②）。既定削除は preventDefault で
  // 止めるため、削除は自前変換の 1 回のみ（ネイティブ削除との二重適用なし）。
  const onCut = useCallback(
    (event: React.ClipboardEvent<HTMLElement>) => {
      if (!isMarkdown) return;
      const el = elementRef.current;
      if (!el) return;
      event.preventDefault();
      const sel = saveEditorSelection(el);
      if (!sel || sel.start === sel.end) return;
      const raw = serializeMarkdownEditorDom(el);
      event.clipboardData.setData("text/plain", raw.slice(sel.start, sel.end));
      commitTransform(el, raw, sel.start, deleteRange(raw, sel.start, sel.end), "struct");
    },
    [isMarkdown, elementRef, commitTransform]
  );

  // ドロップ: プレーンテキストをドロップ位置へ挿入する（既定は無効化し、純粋変換で正準再構築する）。
  // 空テキスト（画像など）のドロップは no-op。ドロップ位置は caretRangeFromPoint で求め、取れなければ
  // 現在の選択位置へ挿入する。
  const onDrop = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      if (!isMarkdown) return;
      const el = elementRef.current;
      if (!el) return;
      event.preventDefault();
      const text = event.dataTransfer.getData("text/plain");
      if (!text) return;
      const raw = serializeMarkdownEditorDom(el);
      const doc = el.ownerDocument as Document & {
        caretRangeFromPoint?: ((x: number, y: number) => Range | null) | undefined;
      };
      const dropRange = doc.caretRangeFromPoint?.(event.clientX, event.clientY) ?? null;
      const sel =
        (dropRange && el.contains(dropRange.startContainer)
          ? rawSelectionFromRange(el, dropRange)
          : null) ?? saveEditorSelection(el);
      if (!sel) return;
      commitTransform(el, raw, sel.start, insertTextAt(raw, sel.start, sel.end, text), "struct");
    },
    [isMarkdown, elementRef, commitTransform]
  );

  // 内部テキストのドラッグ移動は無効化する（onDrop は外部ドロップの挿入のみを担う）。
  // 内部 drag&drop を許すと、元選択の削除と drop 位置への挿入の整合を取る必要があり、
  // onDrop の単純挿入だけでは元テキストが複製されてしまうため、ドラッグ開始自体を止める。
  const onDragStart = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      if (!isMarkdown) return;
      event.preventDefault();
    },
    [isMarkdown]
  );

  // 編集中（contentEditable）に Ctrl/Cmd+クリックで、クリック位置の URL を新規タブで開く。
  // クリックでキャレットが置かれた位置の文字オフセットを求め、その位置に重なる URL を判定する。
  const onClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!isEditing) return;
      if (!(event.ctrlKey || event.metaKey)) return;
      const el = elementRef.current;
      if (!el) return;
      const offset = saveCaret(el);
      if (offset === null) return;
      const url = findUrlTokenAt(readEditor(el), offset);
      if (url && isHttpUrl(url)) {
        event.preventDefault();
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    [isEditing, elementRef, saveCaret, readEditor]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        // 範囲パネルが開いているときは、まずパネルだけを閉じてエディタの選択（キャレット）へ戻す。
        // 続けて押した Escape で従来どおり編集を終了する。
        if (isRangeStyleEnabled && activeRangeRef.current !== null) {
          const el = elementRef.current;
          const start = activeRangeRef.current.start;
          setActiveTextRangeSelection(null);
          if (el) {
            el.focus();
            restoreEditorCaretOffset(el, start);
          }
          return;
        }
        leaveEditMode();
        return;
      }
      if (!isMarkdown) {
        // 非 markdown（セクション等）では Enter キーはブラウザ既定の改行挿入に委ねる（自前で "\n"
        // テキストノードを挿入すると、末尾改行がうまく表示・配置されない contentEditable 既知の問題に
        // 当たるため）。改行の取得は readEditorText（innerText 経由）で行うので、ブラウザが <div> / <br>
        // を挿入しても改行は確実に object.text へ反映される。
        return;
      }
      // ここから markdown モードのキー操作。Enter/改行/削除は beforeinput で横取りするため、ここでは
      // Tab（箇条書きのネスト）と Undo/Redo を扱う。
      // 毎 keydown で「IME 変換中の発火か」を記録する（beforeinput 側の漏れ Enter 識別用）。
      // 確定 Enter は Windows Chrome で key="Process"（keyCode 229）になるためキー名では判定できず、
      // 変換中であること自体を記録する。素の keyCode 229 を単独条件にしないのは、Android 系が
      // 非変換中の実 Enter でも 229 を送る環境があり、実 Enter を全滅させ得るため。
      // ※ 下の composing 早期 return より前に置くこと（後に置くと肝心の IME 由来 keydown が記録されない）。
      lastKeydownWasImeRef.current = composingRef.current || event.nativeEvent.isComposing;
      // IME 変換中（keyCode 229 を含む）は横取りしない。
      if (
        composingRef.current ||
        event.nativeEvent.isComposing ||
        event.nativeEvent.keyCode === 229
      ) {
        return;
      }
      const el = elementRef.current;
      if (event.key === "Tab") {
        event.preventDefault();
        if (!el) return;
        const sel = saveEditorSelection(el);
        if (!sel) return;
        const raw = serializeMarkdownEditorDom(el);
        // Tab / Shift+Tab は箇条書きと番号付きリストのネスト変更（引用・水平線・見出しでは何もしない）。
        const result = changeListIndent(
          raw,
          sel.start,
          sel.end,
          event.shiftKey ? -1 : 1,
          BULLET_MAX_LEVEL
        );
        if (result) commitTransform(el, raw, sel.start, result, "struct");
        return;
      }
      const mod = event.ctrlKey || event.metaKey;
      if (mod && (event.key === "z" || event.key === "Z")) {
        event.preventDefault();
        if (event.shiftKey) performRedo();
        else performUndo();
        return;
      }
      if (mod && (event.key === "y" || event.key === "Y")) {
        event.preventDefault();
        performRedo();
        return;
      }
      // 本文編集中の Ctrl/Command+B は、選択した文字範囲だけの太字へ割り当てる。
      // グローバル側（use-global-shortcuts）は本文編集中の Ctrl 系を処理しない契約のため、
      // ここで扱わないとブラウザ既定の formatBold が正準化として処理され、選択が畳まれる。
      if (mod && !event.altKey && !event.shiftKey && (event.key === "b" || event.key === "B")) {
        // 範囲書式を持たない種別（付箋など）では、既定動作も含めて一切介入しない
        // （これらの種別の入力挙動を変えないため）。
        if (!isRangeStyleEnabled) return;
        // ここから先はテキスト図形。適用可否にかかわらず既定動作は止める
        // （範囲が無いときにブラウザの太字処理で選択が畳まれるのも防ぐ）。
        event.preventDefault();
        // キーリピートで押しっぱなしにしたときに太字が何度も反転しないようにする。
        if (isDisabled || event.repeat) return;
        const range = activeRangeRef.current;
        if (!range) return;
        const applied = toggleTextRangeBold({
          dispatcher,
          objectId,
          range: { end: range.end, start: range.start, text: range.text },
        });
        // 送信できたときだけ、編集器の自前 Undo 履歴へ「変更前の run」を積ませる
        // （範囲専用パネルからの適用と同じ扱い）。
        if (applied) notifyTextRangeStyleApplied();
      }
    },
    [
      isMarkdown,
      elementRef,
      leaveEditMode,
      commitTransform,
      performUndo,
      performRedo,
      isRangeStyleEnabled,
      setActiveTextRangeSelection,
      dispatcher,
      objectId,
      isDisabled,
      notifyTextRangeStyleApplied,
    ]
  );

  const onKeyUp = useCallback(() => {
    if (!isMarkdown) return;
    // keydown 記録（lastKeydownWasImeRef）はその物理キーストロークが生む beforeinput 列（漏れ確定
    // Enter を含む）までが有効期間。keyup で消費し、候補クリック等 Enter を伴わず composition が
    // 終了した後に届く keydown レスの改行入力（音声入力・仮想キーボード等）を stale な記録で
    // 破棄しないようにする。漏れ確定 Enter の beforeinput は同一キーストロークの keyup より
    // 前に着弾するため、ここでの消費は時間窓に依存しない漏れ Enter 破棄を弱めない。
    lastKeydownWasImeRef.current = false;
  }, [isMarkdown]);

  return {
    isEditing,
    enterEditMode,
    leaveEditMode,
    editorProps: {
      contentEditable: isEditing,
      suppressContentEditableWarning: true,
      onInput,
      onCompositionStart,
      onCompositionEnd,
      onBlur,
      onKeyDown,
      onKeyUp,
      onClick,
      onPaste,
      onCut,
      onDrop,
      onDragStart,
    },
  };
}

/**
 * 選択範囲の矩形（ビューポート座標）を求める。複数行選択では全矩形を包含する矩形にする。
 * 面積を持つ有限の矩形が 1 つも無い場合は null（呼び出し側はパネルを閉じる）。
 */
function unionSelectionRect(
  range: Range
): { readonly x: number; readonly y: number; readonly w: number; readonly h: number } | null {
  const rects = [...Array.from(range.getClientRects()), range.getBoundingClientRect()];
  let left = Number.POSITIVE_INFINITY;
  let top = Number.POSITIVE_INFINITY;
  let right = Number.NEGATIVE_INFINITY;
  let bottom = Number.NEGATIVE_INFINITY;
  let found = false;
  for (const rect of rects) {
    if (!Number.isFinite(rect.left) || !Number.isFinite(rect.top)) continue;
    if (!Number.isFinite(rect.width) || !Number.isFinite(rect.height)) continue;
    if (rect.width <= 0 || rect.height <= 0) continue;
    left = Math.min(left, rect.left);
    top = Math.min(top, rect.top);
    right = Math.max(right, rect.right);
    bottom = Math.max(bottom, rect.bottom);
    found = true;
  }
  if (!found) return null;
  return { h: bottom - top, w: right - left, x: left, y: top };
}

/** inputType（横取り対象）を生テキスト + 選択の純粋変換へ写像する。未知の構造入力は正準化のみ。 */
function transformForInput(
  inputType: string,
  raw: string,
  sel: EditorSelection,
  data: string | null
): EditorTextAndCaret | null {
  switch (inputType) {
    case "insertParagraph":
      return splitForEnter(raw, sel.start, sel.end);
    case "insertLineBreak":
      return insertPlainLineBreak(raw, sel.start, sel.end);
    case "insertText":
    case "insertReplacementText":
    case "insertFromComposition":
      return insertTextAt(raw, sel.start, sel.end, data ?? "");
    case "insertFromPaste":
    case "insertFromDrop":
      // 通常は onPaste / onDrop（React 合成）が先に横取りするため到達しないが、防御的に無視する。
      return null;
    case "deleteContentBackward":
    case "deleteWordBackward":
    case "deleteByCut":
    case "deleteContent":
      return sel.start !== sel.end
        ? deleteRange(raw, sel.start, sel.end)
        : deleteBackward(raw, sel.start, BULLET_MAX_LEVEL);
    case "deleteContentForward":
    case "deleteWordForward":
      return sel.start !== sel.end
        ? deleteRange(raw, sel.start, sel.end)
        : deleteForward(raw, sel.start);
    case "deleteByDrag":
      return deleteRange(raw, sel.start, sel.end);
    default:
      // 未知の構造入力は現在の生テキストで正準化のみ（安全側）。
      return { caret: sel.start, text: raw };
  }
}

/**
 * contentEditable の表示テキストを改行付きで取得する。
 *
 * Enter キーでブラウザが挿入する <div> / <br> を textContent は改行へ変換せず連結するため、
 * textContent では改行が失われてしまう（要望②の改行が反映されない不具合の原因）。
 * innerText は「実際に表示されているテキスト」を返し、<div> / <br> 由来の改行を "\n" として
 * 含むため、white-space: pre-wrap の表示と一致した文字列（object.text へ保存する値）を得られる。
 * innerText 非対応の環境（jsdom 等のテスト環境）では textContent にフォールバックする。
 */
function readEditorText(el: HTMLElement): string {
  const rendered = (el as { innerText?: string | undefined }).innerText;
  return typeof rendered === "string" ? rendered : (el.textContent ?? "");
}

/** Unicode 私用領域から、本文に現れない選択境界マーカーを 2 文字選ぶ。 */
function pickSelectionMarkers(text: string): readonly [string, string] | null {
  const markers: string[] = [];
  for (let codePoint = 0xe000; codePoint <= 0xf8ff && markers.length < 2; codePoint += 1) {
    const marker = String.fromCharCode(codePoint);
    if (!text.includes(marker)) markers.push(marker);
  }
  const [start, end] = markers;
  return start !== undefined && end !== undefined ? [start, end] : null;
}

/**
 * 非 Markdown 編集領域の選択両端を、readEditorText と同じ innerText 座標で返す。
 * 終了・開始の順に異なる一時マーカーを置き、両方を置いた後の表示本文を 1 回だけ読む。
 */
function saveSelectionCharacterOffsets(el: HTMLElement): EditorSelection | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return null;
  const markers = pickSelectionMarkers(readEditorText(el));
  if (!markers) return null;
  const [startMarker, endMarker] = markers;
  const endNode = document.createTextNode(endMarker);
  const startNode = document.createTextNode(startMarker);
  const endRange = range.cloneRange();
  const startRange = range.cloneRange();
  endRange.collapse(false);
  endRange.insertNode(endNode);
  startRange.collapse(true);
  startRange.insertNode(startNode);
  try {
    const rendered = readEditorText(el);
    const start = rendered.indexOf(startMarker);
    const endWithMarker = rendered.indexOf(endMarker);
    if (start < 0 || endWithMarker < 0) return null;
    return { end: endWithMarker - startMarker.length, start };
  } finally {
    startNode.remove();
    endNode.remove();
    el.normalize();
  }
}

/** 現在の選択をプレーンテキストで置換し、非対応環境では input も明示的に発火する。 */
function insertPlainTextAtSelection(el: HTMLElement, text: string): boolean {
  if (
    typeof document.execCommand === "function" &&
    document.execCommand("insertText", false, text)
  ) {
    return true;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return false;
  range.deleteContents();
  const textNode = document.createTextNode(text);
  range.insertNode(textNode);
  range.setStartAfter(textNode);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  el.dispatchEvent(new Event("input", { bubbles: true }));
  return true;
}

/** 非 Markdown 編集領域の現在選択を、URL の表示テキスト付きリンクへ置換する。 */
function tryLinkifyPlainSelection(
  el: HTMLElement,
  url: string
): "linkified" | "too-long" | "skipped" {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return "skipped";
  const range = selection.getRangeAt(0);
  if (range.collapsed) return "skipped";
  if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return "skipped";
  const raw = readEditorText(el);
  const offsets = saveSelectionCharacterOffsets(el);
  if (!offsets) return "skipped";
  const plan = planPlainLineLinkReplacement({
    end: offsets.end,
    start: offsets.start,
    text: raw,
    url,
  });
  if (plan.kind === "too-long") return "too-long";
  if (plan.kind !== "replace") return "skipped";
  return insertPlainTextAtSelection(el, plan.insert) ? "linkified" : "skipped";
}

/**
 * 選択開始位置を、要素先頭からの文字数として返す。フォーカスが要素外なら null。
 *
 * 復元側（restoreCaretCharacterOffset）は textContent（改行 "\n" を含む）を走査して数えるが、
 * 保存側で Range.toString() を使うと <div> / <br> の改行を数えず、複数行で桁がずれる（保存と復元で
 * 改行のカウント方法が食い違うため）。これを避けるため、キャレット位置へ一時マーカーを挿入し、
 * readEditorText（innerText 経由で改行を "\n" として含む）上でのインデックスを求めてから元へ戻す。
 * これにより保存・復元が同じ座標系（改行込み）になり、複数行でもキャレット位置が保たれる。
 *
 * マーカーは既存のノードを分割・置き換えずに置く。開始位置がテキストの中なら文字データへ直接差し込み、
 * 要素の境界ならその位置へマーカーのテキストノードを挿入する。どちらも取り除けば DOM と選択が元どおりになる。
 * テキストノードを分割して normalize で結合し直す方式は、開始位置がテキストノードの先頭のとき元のノードを
 * 空にして取り除き、選択していたノードを入れ替えてしまう。選択のドラッグ中は他参加者への中継のたびに
 * 呼ばれるため、その方式では保存した選択の位置（use-bounded-text-selection）が使えなくなる（Issue #160）。
 */
function saveCaretCharacterOffset(el: HTMLElement): number | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const { startContainer, startOffset } = selection.getRangeAt(0);
  if (!el.contains(startContainer)) return null;
  // Unicode 私用領域の 1 文字。通常テキストに現れないため、キャレット位置マーカーとして使う。
  const MARKER = "\uE000";
  const readMarkerIndex = (): number | null => {
    const index = readEditorText(el).indexOf(MARKER);
    return index >= 0 ? index : null;
  };
  if (startContainer instanceof Text) {
    startContainer.insertData(startOffset, MARKER);
    try {
      return readMarkerIndex();
    } finally {
      startContainer.deleteData(startOffset, MARKER.length);
    }
  }
  // コメントなど、文字位置を持たないノードの中の選択は数えない。
  if (startContainer instanceof CharacterData) return null;
  const markerNode = document.createTextNode(MARKER);
  startContainer.insertBefore(markerNode, startContainer.childNodes.item(startOffset));
  try {
    return readMarkerIndex();
  } finally {
    markerNode.remove();
  }
}

/**
 * 要素先頭から offset 文字目へキャレットを移動する。
 * textContent 置換後の単一テキストノードを主に想定しつつ、複数テキストノードでも走査して復元する。
 */
function restoreCaretCharacterOffset(el: HTMLElement, offset: number): void {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  if (!node) {
    // テキストノードが無い（空）の場合は要素先頭へ。
    range.selectNodeContents(el);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    return;
  }
  let remaining = offset;
  while (node) {
    const len = node.textContent?.length ?? 0;
    if (remaining <= len) {
      range.setStart(node, remaining);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    remaining -= len;
    const next = walker.nextNode();
    if (!next) {
      // offset が末尾を超える場合は最後のノード末尾へ寄せる。
      range.setStart(node, len);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    node = next;
  }
}

/**
 * IME 変換開始時の記録から、compositionend で選択が失われていたときの復元先（生テキストオフセット）を求める。
 * compositionend の data は取り消し時に空とは限らない（ブラウザ差）ため使わず、DOM の本文長の差分を
 * 「実際に挿入された長さ」とみなす。変換開始位置を含む行の行末でクランプし、次の行の先頭へ飛ばさない。
 */
function compositionFallbackCaret(raw: string, record: CompositionStartRecord): number {
  const replacedLength = record.end - record.start;
  const insertedLength = raw.length - (record.rawLength - replacedLength);
  const lineBreak = raw.indexOf("\n", record.start);
  const lineEnd = lineBreak < 0 ? raw.length : lineBreak;
  const upper = Math.max(record.start, lineEnd);
  return Math.min(Math.max(record.start + insertedLength, record.start), upper);
}
